// client/src/coms/usePermissions.js
// The current user's effective permissions in a server (optionally in one channel), exactly as the API
// enforces them (GET /servers/:key/permissions/me). Used to hide or disable controls; the server still
// checks every action. Refreshes when permissions change ('vox-permissions-changed': chat stream event
// or a LiveKit permission update) and when the tab becomes visible.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { comsApiGet } from './comsApi';
import { logicalRoomName } from './voip/voiceUtils';

// Any of these opens Server Settings (the server decides which sections: GET /servers/:key/admin).
export const ADMIN_PERMISSION_KEYS = ['manage_server', 'manage_roles', 'manage_channels', 'manage_invites', 'view_audit_log', 'review_reports', 'manage_members', 'kick_members', 'ban_members', 'mute_members', 'warn_members', 'manage_safety', 'manage_automations', 'manage_onboarding', 'manage_announcements', 'manage_events', 'view_insights', 'manage_stickers', 'manage_soundboard', 'manage_soundboard_settings', 'upload_server_sounds', 'manage_storage'];

const cache = new Map(); // url -> { at, promise }
const DEDUPE_MS = 1500;

function fetchPermissions(url, force) {
  const hit = cache.get(url);
  if (!force && hit && Date.now() - hit.at < DEDUPE_MS) return hit.promise;
  const promise = comsApiGet(url).catch(() => null);
  cache.set(url, { at: Date.now(), promise });
  if (cache.size > 200) cache.delete(cache.keys().next().value);
  return promise;
}

export function permissionsUrl(serverKey, kind, channelId) {
  const key = String(serverKey || '').trim();
  if (!key) return '';
  const k = kind === 'voice' ? 'voice' : kind === 'text' ? 'text' : '';
  const id = Number(channelId) || 0;
  return `/servers/${encodeURIComponent(key)}/permissions/me${k && id ? `?kind=${k}&channel=${id}` : ''}`;
}

export function useMyPermissions(serverKey, kind = '', channelId = 0) {
  const url = permissionsUrl(serverKey, kind, channelId);
  const [data, setData] = useState(null);

  const load = useCallback(
    async (force = false) => {
      if (!url) {
        setData(null);
        return;
      }
      const r = await fetchPermissions(url, force);
      setData(r && r.ok ? r : null);
    },
    [url]
  );

  useEffect(() => {
    let alive = true;
    load(false).catch(() => {});
    const onChange = () => {
      if (alive) load(true).catch(() => {});
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') onChange();
    };
    window.addEventListener('vox-permissions-changed', onChange);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      window.removeEventListener('vox-permissions-changed', onChange);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  return useMemo(() => {
    const set = new Set((data && data.permissions) || []);
    return {
      loaded: !!data,
      isOwner: !!(data && data.is_owner),
      timedOutUntil: (data && data.timed_out_until) || 0,
      voiceState: (data && data.voice_state) || { muted: false, deafened: false },
      // Until loaded, report allowed so controls do not flicker; the server enforces regardless.
      has: (key) => (!data ? true : set.has(key)),
      refresh: () => load(true),
    };
  }, [data, load]);
}

// Permissions in the voice channel behind a LiveKit room name (coms_<serverId>_<voiceChannelId>).
export function useVoiceRoomPermissions(roomName) {
  const m = /^coms_(\d+)_(\d+)$/.exec(logicalRoomName(roomName));
  return useMyPermissions(m ? m[1] : '', m ? 'voice' : '', m ? Number(m[2]) : 0);
}
